import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Menu,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Devices as DevicesIcon,
  People as PeopleIcon,
  SupervisedUserCircle as UsersIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Badge as BadgeIcon,
  Add as AddIcon,
  ArrowDropDown as ArrowDropDownIcon,
  AccountTree as StructureIcon,
  Brush as BrandingIcon,
} from '@mui/icons-material';
import BusinessBrandingPanel from './BusinessBrandingPanel';

interface Address {
  id?: string;
  street?: string;
  city?: string;
  state?: string;
  postCode?: string;
  country?: string;
}

interface Business {
  id: string;
  name: string;
  abn: string;
  address?: Address;
  dateCreated: string;
  archivedDate?: string;
  deviceCount: number;
  contactCount: number;
}

interface Device {
  id: string;
  name: string;
  deviceTypeId: string;
  deviceTypeName?: string;
  serialNumber?: string;
  isActive: boolean;
  lastSeen?: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  businessContactType?: string;
}

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
}

interface DeviceType {
  id: string;
  name: string;
}

interface BusinessAssociation {
  id: string;
  parentBusinessId: string;
  parentBusinessName: string;
  childBusinessId: string;
  childBusinessName: string;
  startDate?: string;
  archivedDate?: string;
}

interface BusinessListItem {
  id: string;
  name: string;
  abn: string;
}

interface AvailableDevice {
  id: string;
  name: string;
  deviceTypeId: string;
  deviceTypeName?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`business-tabpanel-${index}`}
      aria-labelledby={`business-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ px: 2, py: 1.5 }}>{children}</Box>}
    </div>
  );
}

interface BusinessDetailsProps {
  businessId: string;
  onBack: () => void;
}

const BusinessDetails: React.FC<BusinessDetailsProps> = ({ businessId, onBack }) => {
  const auth = useAuth();
  const [business, setBusiness] = useState<Business | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [loadingTab, setLoadingTab] = useState(false);
  const [openAddDeviceDialog, setOpenAddDeviceDialog] = useState(false);
  const [availableDevices, setAvailableDevices] = useState<AvailableDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [openCreateDeviceDialog, setOpenCreateDeviceDialog] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [newDeviceTypeId, setNewDeviceTypeId] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<Array<{ id: string; email: string; fullName?: string }>>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [parentAssociation, setParentAssociation] = useState<BusinessAssociation | null>(null);
  const [childAssociations, setChildAssociations] = useState<BusinessAssociation[]>([]);
  const [structureLoaded, setStructureLoaded] = useState(false);
  const [openAddChildDialog, setOpenAddChildDialog] = useState(false);
  const [availableBusinesses, setAvailableBusinesses] = useState<BusinessListItem[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string>('');

  const fetchBusiness = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/businesses/${businessId}`, {
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch business details');
      }

      const data = await response.json();
      setBusiness(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    try {
      setLoadingTab(true);
      const response = await fetch(`/api/businesses/${businessId}/devices`, {
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch devices');
      }

      const data = await response.json();
      setDevices(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoadingTab(false);
    }
  };

  const fetchContacts = async () => {
    try {
      setLoadingTab(true);
      const response = await fetch(`/api/businesses/${businessId}/contacts`, {
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch contacts');
      }

      const data = await response.json();
      setContacts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoadingTab(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingTab(true);
      const response = await fetch(`/api/businesses/${businessId}/users`, {
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoadingTab(false);
    }
  };

  const fetchStructure = async () => {
    try {
      setLoadingTab(true);
      const [parentResponse, childrenResponse] = await Promise.all([
        fetch(`/api/businesses/${businessId}/parent`, {
          headers: { Authorization: `Bearer ${auth.tokens?.accessToken}` },
        }),
        fetch(`/api/businesses/${businessId}/children`, {
          headers: { Authorization: `Bearer ${auth.tokens?.accessToken}` },
        }),
      ]);

      if (!parentResponse.ok || !childrenResponse.ok) {
        throw new Error('Failed to fetch business structure');
      }

      // /parent returns 204 (empty body) rather than a JSON `null` when there is no parent
      const parentText = await parentResponse.text();
      const childrenData = await childrenResponse.json();
      setParentAssociation(parentText ? JSON.parse(parentText) : null);
      setChildAssociations(childrenData);
      setStructureLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoadingTab(false);
    }
  };

  const fetchAvailableBusinessesForChild = async () => {
    try {
      const response = await fetch('/api/businesses/list', {
        headers: { Authorization: `Bearer ${auth.tokens?.accessToken}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch businesses');
      }

      const data: BusinessListItem[] = await response.json();
      const childIds = new Set(childAssociations.map((c) => c.childBusinessId));
      setAvailableBusinesses(data.filter((b) => b.id !== businessId && !childIds.has(b.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setAvailableBusinesses([]);
    }
  };

  const handleAddChild = async () => {
    if (!selectedChildId) return;

    try {
      const response = await fetch(`/api/businesses/${businessId}/children`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify({ childBusinessId: selectedChildId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add child business');
      }

      await fetchStructure();
      setOpenAddChildDialog(false);
      setSelectedChildId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRemoveChild = async (childId: string) => {
    if (!confirm('Are you sure you want to remove this business from the group?')) {
      return;
    }

    try {
      const response = await fetch(`/api/businesses/${businessId}/children/${childId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${auth.tokens?.accessToken}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove child business');
      }

      await fetchStructure();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const fetchAvailableDevices = async () => {
    try {
      const response = await fetch('/api/devices', {
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch available devices');
      }

      const data = await response.json();
      // Filter out devices already associated with this business
      const deviceIds = new Set(devices.map(d => d.id));
      const available = data.filter((d: Device) => !deviceIds.has(d.id));
      setAvailableDevices(available);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleAddDevice = async () => {
    if (!selectedDeviceId) return;

    try {
      const response = await fetch(`/api/businesses/${businessId}/devices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify({ deviceId: selectedDeviceId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add device');
      }

      // Refresh devices and business data
      await fetchDevices();
      await fetchBusiness();
      setOpenAddDeviceDialog(false);
      setSelectedDeviceId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleOpenAddDeviceDialog = () => {
    fetchAvailableDevices();
    setOpenAddDeviceDialog(true);
    setAnchorEl(null);
  };

  const fetchDeviceTypes = async () => {
    try {
      const response = await fetch('/api/device-types', {
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch device types');
      }

      const data = await response.json();
      setDeviceTypes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleOpenCreateDeviceDialog = () => {
    fetchDeviceTypes();
    setOpenCreateDeviceDialog(true);
    setAnchorEl(null);
  };

  const handleCreateDevice = async () => {
    if (!newDeviceName || !newDeviceTypeId) return;

    try {
      // Create the device
      const createResponse = await fetch('/api/devices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify({
          name: newDeviceName,
          deviceTypeId: newDeviceTypeId,
        }),
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create device');
      }

      const newDevice = await createResponse.json();

      // Add the device to the business
      const addResponse = await fetch(`/api/businesses/${businessId}/devices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify({ deviceId: newDevice.id }),
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add device to business');
      }

      // Refresh devices and business data
      await fetchDevices();
      await fetchBusiness();
      setOpenCreateDeviceDialog(false);
      setNewDeviceName('');
      setNewDeviceTypeId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch available users');
      }

      const data = await response.json();
      const existingIds = new Set(users.map((u) => u.id));
      setAvailableUsers((data || []).filter((u: { id: string }) => !existingIds.has(u.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setAvailableUsers([]);
    }
  };

  const handleAddUser = async () => {
    if (!selectedUserId) return;

    try {
      const response = await fetch(`/api/businesses/${businessId}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify({ userId: selectedUserId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add user');
      }

      // Refresh users and business data
      await fetchUsers();
      await fetchBusiness();
      setOpenAddUserDialog(false);
      setSelectedUserId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!confirm('Are you sure you want to remove this user from the business?')) {
      return;
    }

    try {
      const response = await fetch(`/api/businesses/${businessId}/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove user');
      }

      // Refresh users
      await fetchUsers();
      await fetchBusiness();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRemoveDevice = async (deviceId: string) => {
    if (!confirm('Are you sure you want to remove this device from the business?')) {
      return;
    }

    try {
      const response = await fetch(`/api/businesses/${businessId}/devices/${deviceId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove device');
      }

      // Refresh devices
      await fetchDevices();
      await fetchBusiness();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  useEffect(() => {
    if (auth.isSignedIn && auth.tokens?.accessToken) {
      fetchBusiness();
      fetchDevices();
    }
  }, [businessId, auth.isSignedIn, auth.tokens?.accessToken]);

  useEffect(() => {
    if (tabValue === 1 && contacts.length === 0) {
      fetchContacts();
    } else if (tabValue === 2 && users.length === 0) {
      fetchUsers();
    } else if (tabValue === 3 && !structureLoaded) {
      fetchStructure();
    }
  }, [tabValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!business) {
    return (
      <Box>
        <Alert severity="error">Business not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h2">
          Business Details
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Business Details */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessIcon sx={{ mr: 1, fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h6">{business.name}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <BadgeIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  ABN
                </Typography>
                <Typography variant="body1">{business.abn}</Typography>
              </Box>

              {business.address && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    <LocationIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Address
                  </Typography>
                  <Typography variant="body2">
                    {business.address.street && (
                      <>
                        {business.address.street}
                        <br />
                      </>
                    )}
                    {business.address.city && business.address.state
                      ? `${business.address.city}, ${business.address.state}`
                      : business.address.city || business.address.state}
                    {business.address.postCode && (
                      <>
                        {' '}
                        {business.address.postCode}
                      </>
                    )}
                    {business.address.country && (
                      <>
                        <br />
                        {business.address.country}
                      </>
                    )}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  <CalendarIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  Created
                </Typography>
                <Typography variant="body2">
                  {new Date(business.dateCreated).toLocaleDateString()}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  {business.archivedDate ? (
                    <Chip label="Archived" size="small" color="default" />
                  ) : (
                    <Chip label="Active" size="small" color="success" />
                  )}
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Devices
                  </Typography>
                  <Chip label={business.deviceCount} size="small" color="primary" />
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Contacts
                  </Typography>
                  <Chip label={business.contactCount} size="small" color="secondary" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Tabs */}
        <Grid item xs={12} md={8}>
          <Paper>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="business tabs">
              <Tab icon={<DevicesIcon />} label="Devices" iconPosition="start" />
              <Tab icon={<PeopleIcon />} label="Contacts" iconPosition="start" />
              <Tab icon={<UsersIcon />} label="Users" iconPosition="start" />
              <Tab icon={<StructureIcon />} label="Structure" iconPosition="start" />
              <Tab icon={<BrandingIcon />} label="Branding" iconPosition="start" />
            </Tabs>

            {loadingTab ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TabPanel value={tabValue} index={0}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="h6">Devices</Typography>
                    <Box>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        endIcon={<ArrowDropDownIcon />}
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        size="small"
                      >
                        Add Device
                      </Button>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                      >
                        <MenuItem onClick={handleOpenAddDeviceDialog}>
                          Add Existing Device
                        </MenuItem>
                        <MenuItem onClick={handleOpenCreateDeviceDialog}>
                          Create New Device
                        </MenuItem>
                      </Menu>
                    </Box>
                  </Box>
                  <TableContainer>
                    <Table sx={{ '& .MuiTableCell-root': { py: 1 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Actions</TableCell>
                          <TableCell>Device Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Serial Number</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Last Seen</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {devices.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography variant="body2" color="text.secondary" py={4}>
                                No devices found for this business.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          devices.map((device) => (
                            <TableRow key={device.id}>
                              <TableCell>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveDevice(device.id)}
                                >
                                  Remove
                                </Button>
                              </TableCell>
                              <TableCell>{device.name}</TableCell>
                              <TableCell>{device.deviceTypeName || '-'}</TableCell>
                              <TableCell>{device.serialNumber || '-'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={device.isActive ? 'Active' : 'Inactive'}
                                  size="small"
                                  color={device.isActive ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell>
                                {device.lastSeen
                                  ? new Date(device.lastSeen).toLocaleString()
                                  : 'Never'}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {contacts.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" color="text.secondary" py={4}>
                                No contacts found for this business.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          contacts.map((contact) => (
                            <TableRow key={contact.id}>
                              <TableCell>
                                {contact.firstName} {contact.lastName}
                              </TableCell>
                              <TableCell>{contact.email}</TableCell>
                              <TableCell>{contact.phone || '-'}</TableCell>
                              <TableCell>
                                {contact.businessContactType ? (
                                  <Chip label={contact.businessContactType} size="small" />
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Users</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        fetchAvailableUsers();
                        setOpenAddUserDialog(true);
                      }}
                    >
                      Add User
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Email</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Roles</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {users.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" color="text.secondary" py={4}>
                                No users found for this business.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          users.map((user) => (
                            <TableRow key={user.id}>
                              <TableCell>{user.email}</TableCell>
                              <TableCell>
                                {user.firstName && user.lastName
                                  ? `${user.firstName} ${user.lastName}`
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                  {user.roles.map((role) => (
                                    <Chip key={role} label={role} size="small" color="primary" />
                                  ))}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveUser(user.id)}
                                >
                                  Remove
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={3}>
                  {parentAssociation && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Parent Business
                      </Typography>
                      <Chip
                        icon={<BusinessIcon />}
                        label={parentAssociation.parentBusinessName}
                        variant="outlined"
                      />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Child Businesses</Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        fetchAvailableBusinessesForChild();
                        setOpenAddChildDialog(true);
                      }}
                    >
                      Add Child Business
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Since</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {childAssociations.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              <Typography variant="body2" color="text.secondary" py={4}>
                                This business has no child businesses.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          childAssociations.map((child) => (
                            <TableRow key={child.id}>
                              <TableCell>{child.childBusinessName}</TableCell>
                              <TableCell>
                                {child.startDate ? new Date(child.startDate).toLocaleDateString() : 'Unknown'}
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveChild(child.childBusinessId)}
                                >
                                  Remove
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={4}>
                  <BusinessBrandingPanel businessId={businessId} businessName={business?.name ?? ''} />
                </TabPanel>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Add Device Dialog */}
      <Dialog
        open={openAddDeviceDialog}
        onClose={() => {
          setOpenAddDeviceDialog(false);
          setSelectedDeviceId('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Device to Business</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select Device</InputLabel>
              <Select
                value={selectedDeviceId}
                onChange={(e) => setSelectedDeviceId(e.target.value)}
                label="Select Device"
              >
                {availableDevices.length === 0 ? (
                  <MenuItem disabled>
                    <em>No available devices</em>
                  </MenuItem>
                ) : (
                  availableDevices.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                      {device.deviceTypeName && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          ({device.deviceTypeName})
                        </Typography>
                      )}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenAddDeviceDialog(false);
              setSelectedDeviceId('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddDevice}
            variant="contained"
            disabled={!selectedDeviceId}
          >
            Add Device
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Device Dialog */}
      <Dialog
        open={openCreateDeviceDialog}
        onClose={() => {
          setOpenCreateDeviceDialog(false);
          setNewDeviceName('');
          setNewDeviceTypeId('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Device</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Device Name"
              value={newDeviceName}
              onChange={(e) => setNewDeviceName(e.target.value)}
              required
            />
            <FormControl fullWidth required>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={newDeviceTypeId}
                onChange={(e) => setNewDeviceTypeId(e.target.value)}
                label="Device Type"
              >
                {deviceTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenCreateDeviceDialog(false);
              setNewDeviceName('');
              setNewDeviceTypeId('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateDevice}
            variant="contained"
            disabled={!newDeviceName || !newDeviceTypeId}
          >
            Create & Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog
        open={openAddUserDialog}
        onClose={() => {
          setOpenAddUserDialog(false);
          setSelectedUserId('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add User to Business</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                label="Select User"
              >
                {availableUsers.length === 0 ? (
                  <MenuItem disabled>
                    <em>No available users</em>
                  </MenuItem>
                ) : (
                  availableUsers.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.fullName ? `${user.fullName} — ${user.email}` : user.email}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenAddUserDialog(false);
              setSelectedUserId('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddUser}
            variant="contained"
            disabled={!selectedUserId}
          >
            Add User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Child Business Dialog */}
      <Dialog
        open={openAddChildDialog}
        onClose={() => {
          setOpenAddChildDialog(false);
          setSelectedChildId('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Child Business</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select Business</InputLabel>
              <Select
                value={selectedChildId}
                onChange={(e) => setSelectedChildId(e.target.value)}
                label="Select Business"
              >
                {availableBusinesses.length === 0 ? (
                  <MenuItem disabled>
                    <em>No available businesses</em>
                  </MenuItem>
                ) : (
                  availableBusinesses.map((b) => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (ABN {b.abn})
                      </Typography>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenAddChildDialog(false);
              setSelectedChildId('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddChild}
            variant="contained"
            disabled={!selectedChildId}
          >
            Add Child Business
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BusinessDetails;
